import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    IdentificationBadge,
    Users,
    Trophy,
    Key,
    SignOut,
    FloppyDisk,
    UserPlus,
    Link as LinkIcon,
    X,
} from "@phosphor-icons/react";

const roleLabels = {
    admin: "Amministratore",
    coach: "Allenatore",
    player: "Giocatore",
    parent: "Genitore",
};

export default function Profile() {
    const { user, logout } = useAuth();
    const [teams, setTeams] = useState([]);
    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/teams").catch(() => ({ data: [] }));
                setTeams(data);
            } catch (e) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("teams load failed:", e);
                }
            }
        })();
    }, []);

    const teamName = (id) => teams.find((t) => t.id === id)?.name || "-";

    const changePassword = async (e) => {
        e.preventDefault();
        if (pw.next !== pw.confirm) {
            toast.error("Le nuove password non coincidono");
            return;
        }
        if (pw.next.length < 6) {
            toast.error("La nuova password deve avere almeno 6 caratteri");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: pw.current,
                new_password: pw.next,
            });
            toast.success("Password aggiornata");
            setPw({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="profile-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    Il tuo account
                </div>
                <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
                    Profilo
                </h1>
            </div>

            <div className="rounded-2xl hairline bg-card p-5 flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-primary/15 text-primary grid place-items-center font-display text-2xl font-black">
                    {user?.name
                        ?.split(" ")
                        .map((s) => s[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-display text-2xl font-bold">{user?.name}</div>
                    <div className="text-sm text-muted-foreground truncate">{user?.email}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] mt-1 text-primary">
                        {roleLabels[user?.role]}
                    </div>
                </div>
            </div>

            {user?.role === "player" && <PlayerSelfForm teams={teams} />}
            {user?.role === "parent" && <ParentChildrenForm teamName={teamName} />}
            {user?.role === "coach" && (
                <div className="rounded-2xl hairline bg-card p-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">
                        <Trophy size={14} weight="fill" /> La tua squadra
                    </div>
                    <div className="font-display text-xl font-bold mt-1">
                        {teamName(user.team_id)}
                    </div>
                </div>
            )}

            {/* Change password */}
            <form
                onSubmit={changePassword}
                className="rounded-2xl hairline bg-card p-5 space-y-3"
                data-testid="change-password-section"
            >
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">
                    <Key size={14} weight="fill" /> Sicurezza
                </div>
                <div className="font-display text-xl font-bold">Cambia password</div>
                <div className="grid gap-3 md:grid-cols-3">
                    <div className="space-y-1">
                        <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                            Password attuale
                        </Label>
                        <Input
                            data-testid="pw-current-input"
                            type="password"
                            value={pw.current}
                            onChange={(e) => setPw({ ...pw, current: e.target.value })}
                            required
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                            Nuova password
                        </Label>
                        <Input
                            data-testid="pw-new-input"
                            type="password"
                            value={pw.next}
                            onChange={(e) => setPw({ ...pw, next: e.target.value })}
                            required
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                            Conferma
                        </Label>
                        <Input
                            data-testid="pw-confirm-input"
                            type="password"
                            value={pw.confirm}
                            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                            required
                        />
                    </div>
                </div>
                <div className="flex justify-end">
                    <Button type="submit" data-testid="change-password-submit" disabled={saving} className="press">
                        {saving ? "Aggiorno..." : "Aggiorna password"}
                    </Button>
                </div>
            </form>

            <div className="rounded-2xl hairline bg-card p-5 flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Sessione
                    </div>
                    <div className="font-display text-lg font-bold mt-1">Esci dall&apos;account</div>
                </div>
                <Button
                    data-testid="logout-btn-profile"
                    variant="outline"
                    onClick={() => {
                        logout();
                        window.location.href = "/login";
                    }}
                    className="press"
                >
                    <SignOut size={16} weight="bold" className="mr-2" /> Esci
                </Button>
            </div>
        </div>
    );
}

function PlayerSelfForm({ teams }) {
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        birth_year: "",
        team_id: "",
        jersey_number: "",
        position: "",
    });
    const [parentEmails, setParentEmails] = useState([]);
    const [emailInput, setEmailInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [linkedParents, setLinkedParents] = useState([]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/players/me");
            if (data) {
                setForm({
                    first_name: data.first_name || "",
                    last_name: data.last_name || "",
                    birth_year: data.birth_year || "",
                    team_id: data.team_id || "",
                    jersey_number: data.jersey_number ?? "",
                    position: data.position || "",
                });
                setLinkedParents(data.parents || []);
                setParentEmails((data.parents || []).map((p) => p.email));
            }
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("player me load failed:", e);
            }
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const addEmail = () => {
        const e = emailInput.trim().toLowerCase();
        if (!e) return;
        if (parentEmails.includes(e)) {
            toast.info("Email già aggiunta");
            return;
        }
        setParentEmails([...parentEmails, e]);
        setEmailInput("");
    };

    const removeEmail = (e) => {
        setParentEmails(parentEmails.filter((x) => x !== e));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.put("/players/me", {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                birth_year: form.birth_year ? parseInt(form.birth_year) : null,
                team_id: form.team_id || null,
                jersey_number: form.jersey_number ? parseInt(form.jersey_number) : null,
                position: form.position || null,
                parent_emails: parentEmails,
            });
            toast.success("Profilo aggiornato");
            setLinkedParents(data.parents || []);
            const unknown = data.unknown_parent_emails || [];
            if (unknown.length > 0) {
                toast.warning(
                    `Genitori non trovati: ${unknown.join(", ")} - devono prima registrarsi come genitore`
                );
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="rounded-2xl hairline bg-card p-5 space-y-4"
            data-testid="player-self-form"
        >
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">
                <IdentificationBadge size={14} weight="fill" /> Scheda giocatore
            </div>
            <div className="grid gap-3 md:grid-cols-2">
                <Field label="Nome">
                    <Input
                        data-testid="player-first-name"
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        required
                    />
                </Field>
                <Field label="Cognome">
                    <Input
                        data-testid="player-last-name"
                        value={form.last_name}
                        onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                        required
                    />
                </Field>
                <Field label="Anno di nascita">
                    <Input
                        data-testid="player-birth-year"
                        type="number"
                        min="1980"
                        max="2020"
                        placeholder="es. 2008"
                        value={form.birth_year}
                        onChange={(e) => setForm({ ...form, birth_year: e.target.value })}
                    />
                </Field>
                <Field label="Numero maglia">
                    <Input
                        data-testid="player-jersey-number"
                        type="number"
                        min="1"
                        max="99"
                        value={form.jersey_number}
                        onChange={(e) => setForm({ ...form, jersey_number: e.target.value })}
                    />
                </Field>
                <Field label="Squadra">
                    <Select
                        value={form.team_id || "none"}
                        onValueChange={(v) =>
                            setForm({ ...form, team_id: v === "none" ? "" : v })
                        }
                    >
                        <SelectTrigger data-testid="player-team-select">
                            <SelectValue placeholder="Scegli la squadra" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">Nessuna</SelectItem>
                            {teams.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                    {t.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Ruolo in campo">
                    <Select
                        value={form.position || "none"}
                        onValueChange={(v) =>
                            setForm({ ...form, position: v === "none" ? "" : v })
                        }
                    >
                        <SelectTrigger data-testid="player-position-select">
                            <SelectValue placeholder="Scegli il ruolo" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">Nessuno</SelectItem>
                            <SelectItem value="Portiere">Portiere</SelectItem>
                            <SelectItem value="Difensore">Difensore</SelectItem>
                            <SelectItem value="Centrocampista">Centrocampista</SelectItem>
                            <SelectItem value="Attaccante">Attaccante</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
            </div>

            <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Genitori collegati (email)
                </Label>
                <div className="flex gap-2">
                    <Input
                        data-testid="player-parent-email-input"
                        type="email"
                        placeholder="email.genitore@esempio.it"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                addEmail();
                            }
                        }}
                    />
                    <Button
                        type="button"
                        variant="outline"
                        onClick={addEmail}
                        data-testid="player-add-parent"
                        className="press"
                    >
                        <UserPlus size={14} weight="bold" className="mr-1" /> Aggiungi
                    </Button>
                </div>
                {parentEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {parentEmails.map((em) => {
                            const linked = linkedParents.find((p) => p.email === em);
                            return (
                                <span
                                    key={em}
                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs hairline ${
                                        linked ? "bg-primary/15 text-primary" : "bg-muted"
                                    }`}
                                >
                                    {linked ? linked.name : em}
                                    <button
                                        type="button"
                                        onClick={() => removeEmail(em)}
                                        className="opacity-70 hover:opacity-100 press"
                                        aria-label="Rimuovi"
                                    >
                                        <X size={11} weight="bold" />
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}
                <div className="text-[11px] text-muted-foreground">
                    Il genitore deve essersi registrato come &quot;Genitore&quot; con quell&apos;email
                    perché il collegamento venga effettuato.
                </div>
            </div>

            <div className="flex justify-end">
                <Button
                    type="submit"
                    data-testid="player-save-btn"
                    disabled={saving}
                    className="press"
                >
                    <FloppyDisk size={14} weight="fill" className="mr-2" />
                    {saving ? "Salvataggio..." : "Salva profilo"}
                </Button>
            </div>
        </form>
    );
}

function ParentChildrenForm({ teamName }) {
    const [children, setChildren] = useState([]);
    const [email, setEmail] = useState("");
    const [linking, setLinking] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/players/children");
            setChildren(data);
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("children load failed:", e);
            }
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const link = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLinking(true);
        try {
            await api.post("/players/link-child", {
                player_email: email.trim().toLowerCase(),
            });
            toast.success("Figlio collegato");
            setEmail("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLinking(false);
        }
    };

    const unlink = async (pid) => {
        if (!window.confirm("Rimuovere il collegamento a questo figlio?")) return;
        try {
            await api.delete(`/players/link-child/${pid}`);
            toast.success("Collegamento rimosso");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="rounded-2xl hairline bg-card p-5 space-y-4" data-testid="parent-children-form">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">
                <Users size={14} weight="fill" /> I tuoi figli
            </div>

            <form onSubmit={link} className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Collega figlio tramite email
                </Label>
                <div className="flex gap-2">
                    <Input
                        data-testid="parent-child-email-input"
                        type="email"
                        placeholder="email.giocatore@esempio.it"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                    <Button
                        type="submit"
                        data-testid="parent-link-child-btn"
                        disabled={linking}
                        className="press"
                    >
                        <LinkIcon size={14} weight="bold" className="mr-1" />
                        {linking ? "..." : "Collega"}
                    </Button>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    Il giocatore deve essersi già registrato con quell&apos;email e aver compilato
                    il proprio profilo.
                </div>
            </form>

            <div className="space-y-2 pt-2">
                {children.length === 0 && (
                    <div className="text-sm text-muted-foreground py-2">
                        Nessun figlio collegato. Usa la casella sopra per iniziare.
                    </div>
                )}
                {children.map((c) => (
                    <div
                        key={c.id}
                        className="rounded-xl hairline p-3 flex items-center gap-3"
                        data-testid={`child-${c.id}`}
                    >
                        <div className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-bold">
                            {c.first_name?.[0]}{c.last_name?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">
                                {c.first_name} {c.last_name}
                                {c.jersey_number != null && (
                                    <span className="text-muted-foreground ml-2 text-sm">
                                        #{c.jersey_number}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                                {teamName(c.team_id)}
                                {c.position && ` · ${c.position}`}
                                {c.birth_year && ` · ${c.birth_year}`}
                            </div>
                            {c.email && (
                                <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                                    {c.email}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => unlink(c.id)}
                            className="p-2 rounded-lg hover:bg-destructive/10 text-destructive press"
                            aria-label="Rimuovi"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                {label}
            </Label>
            {children}
        </div>
    );
}
